import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { money } from "@/lib/format";
import { ErrorState, StatusBadge } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Trophy, Truck, Package, CalendarClock, ArrowRight, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

const STAGE_ORDER = ["new", "following_up", "won", "booked", "lost"];
const STAGE_BAR = { new: "bg-[#0284C7]", following_up: "bg-[#D97706]", won: "bg-[#059669]", booked: "bg-[#7C3AED]", lost: "bg-[#DC2626]" };

const DSTATUS = {
  pending: { l: "Planned", c: "bg-slate-100 text-slate-600" },
  packed: { l: "Packed", c: "bg-blue-100 text-blue-700" },
  dispatched: { l: "Out for delivery", c: "bg-amber-100 text-amber-700" },
  delivered: { l: "Delivered", c: "bg-emerald-100 text-emerald-700" },
};
const methodLabel = (m, o, type) => (type === "pickup" ? "Pickup" : ({ needafork: "NeedAFork", van: "Van" }[m] || (m === "other" ? (o || "Other") : "—")));
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return d; } };

function HeroKpi({ label, value, sub, tone = "default", onClick, testid }) {
  const bg = { default: "bg-white/[0.06] hover:bg-white/[0.1]", won: "bg-emerald-400/[0.14] hover:bg-emerald-400/20", deliveries: "bg-amber-400/[0.16] hover:bg-amber-400/25", ships: "bg-indigo-400/[0.16] hover:bg-indigo-400/25" }[tone];
  const lblc = { default: "text-slate-400", won: "text-emerald-200", deliveries: "text-amber-200", ships: "text-indigo-200" }[tone];
  return (
    <button onClick={onClick} data-testid={testid} className={cn("rounded-2xl p-5 text-left transition-colors", bg)}>
      <div className={cn("text-[10px] font-bold uppercase tracking-[0.14em]", lblc)}>{label}</div>
      <div className="mt-1.5 font-display text-2xl font-bold text-white sm:text-3xl">{value}</div>
      <div className={cn("mt-1 text-[11px]", lblc)}>{sub}</div>
    </button>
  );
}

function SectionHead({ icon: Icon, title, subtitle, accent = "primary", onView }) {
  const chip = { primary: "bg-primary/10 text-primary", amber: "bg-amber-100 text-amber-600", blue: "bg-blue-100 text-blue-600", indigo: "bg-indigo-100 text-indigo-600", warning: "bg-amber-100 text-amber-600", emerald: "bg-emerald-100 text-emerald-600" }[accent];
  return (
    <div className="mb-4 flex items-start justify-between gap-2 border-b border-slate-100 pb-3.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl", chip)}><Icon className="h-[18px] w-[18px]" strokeWidth={1.9} /></span>
        <div className="min-w-0">
          <h2 className="truncate font-display text-sm font-bold leading-tight text-ink">{title}</h2>
          {subtitle && <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      {onView && <button onClick={onView} aria-label="View all" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-slate-100 hover:text-primary"><ArrowRight className="h-4 w-4" /></button>}
    </div>
  );
}

function Tile({ icon, title, subtitle, accent = "primary", onView, children, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
      <SectionHead icon={icon} title={title} subtitle={subtitle} accent={accent} onView={onView} />
      <div className="flex-1">{children}</div>
    </div>
  );
}

const Empty = ({ children }) => <div className="rounded-lg bg-slate-50/80 px-3 py-2.5 text-center text-xs text-muted-foreground">{children}</div>;
const Chip = ({ s }) => { const m = DSTATUS[s] || DSTATUS.pending; return <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold", m.c)}>{m.l}</span>; };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";
  const [range, setRange] = useState("this_month");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["dashboard", range], queryFn: async () => (await client.get("/dashboard", { params: { range } })).data });

  if (isError) return <div className="animate-fade-up"><ErrorState onRetry={refetch} /></div>;

  const d = data || {};
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const stageMax = Math.max(1, ...STAGE_ORDER.map((s) => d.stages?.[s]?.value || 0));
  const rangeLabel = { this_month: "this month", last_month: "last month", this_quarter: "this quarter" }[range] || "this month";
  const delToday = d.deliveries_today || [];
  const dcount = { pending: 0, packed: 0, dispatched: 0, delivered: 0 };
  delToday.forEach((x) => { dcount[x.status] = (dcount[x.status] || 0) + 1; });

  if (isLoading) return (
    <div className="animate-fade-up space-y-6">
      <div className="h-56 animate-pulse rounded-3xl bg-muted/50" />
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-muted/40" />)}</div>
    </div>
  );

  return (
    <div className="animate-fade-up space-y-6">
      {/* Hero */}
      <div className="rounded-3xl bg-[#0f1e38] p-7 sm:p-9" data-testid="dashboard-hero">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{greeting}, {firstName} 👋</h1>
            <p className="mt-1.5 text-sm text-slate-400">{today} · here's your day at a glance</p>
          </div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[160px] border-white/15 bg-white/10 text-white" data-testid="dashboard-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="this_quarter">This quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <HeroKpi label="Active pipeline" value={money(d.active_value)} sub={`${d.active_count ?? 0} open leads`} onClick={() => navigate("/pipeline")} testid="kpi-pipeline" />
          <HeroKpi label={`Won ${rangeLabel}`} value={money(d.won_value_month)} sub={`${d.won_month ?? 0} deals · ${d.conversion ?? 0}%`} tone="won" testid="kpi-won" />
          <HeroKpi label="Deliveries today" value={d.deliveries_today_count ?? 0} sub={`${d.out_for_delivery ?? 0} out · ${d.delivered_today ?? 0} delivered`} tone="deliveries" onClick={() => navigate("/deliveries")} testid="kpi-deliveries" />
          <HeroKpi label="Overdue follow-ups" value={d.overdue ?? 0} sub={`${d.due_today ?? 0} due today`} onClick={() => navigate("/followups")} testid="kpi-overdue" />
        </div>
      </div>

      {/* Operations */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        {/* Deliveries + Dispatch (stacked) */}
        <div className="flex flex-col gap-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="tile-deliveries">
          <div>
            <SectionHead icon={Truck} accent="amber" title="Deliveries today" subtitle={`${delToday.length} scheduled`} onView={() => navigate("/deliveries")} />
            <div className="mb-3 grid grid-cols-4 gap-2 text-center">
              {[["Planned", dcount.pending, "bg-slate-50 text-slate-600"], ["Packed", dcount.packed, "bg-blue-50 text-blue-700"], ["Out for delivery", dcount.dispatched, "bg-amber-50 text-amber-700"], ["Delivered", dcount.delivered, "bg-emerald-50 text-emerald-700"]].map(([l, v, c]) => (
                <div key={l} className={cn("rounded-xl py-3", c)}><div className="font-display text-2xl font-bold">{v}</div><div className="mt-0.5 text-[9px] font-bold uppercase leading-tight tracking-wide opacity-80">{l}</div></div>
              ))}
            </div>
            {delToday.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {delToday.slice(0, 6).map((x) => (
                  <button key={x.id} onClick={() => navigate("/deliveries")} data-testid={`dash-delivery-${x.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2.5 text-left transition-colors hover:border-primary/30">
                    <div className="min-w-0"><div className="truncate text-sm font-semibold text-ink">{x.company || x.order_number}</div><div className="truncate text-[11px] text-muted-foreground">{x.delivery_address || methodLabel(x.delivery_method, x.delivery_method_other, x.fulfil_type)}</div></div>
                    <Chip s={x.status} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <SectionHead icon={Package} accent="blue" title="Dispatch queue" subtitle={`${(d.dispatch_queue || []).length} to dispatch`} onView={() => navigate("/dispatch")} />
            {(d.dispatch_queue || []).length === 0 ? <Empty>Nothing in the queue — all caught up.</Empty> : (
              <div className="grid gap-2 sm:grid-cols-2">
                {(d.dispatch_queue || []).slice(0, delToday.length === 0 ? 12 : 6).map((x) => (
                  <button key={x.id} onClick={() => navigate("/dispatch")} data-testid={`dash-dispatch-${x.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2.5 text-left transition-colors hover:border-primary/30">
                    <div className="min-w-0"><div className="truncate font-mono text-xs font-bold text-ink">{x.order_number}</div><div className="truncate text-[11px] text-muted-foreground">{x.fulfil_date ? fmtDay(x.fulfil_date) : "No date"} · {methodLabel(x.delivery_method, x.delivery_method_other, x.fulfil_type)}</div></div>
                    <Chip s={x.status} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Follow-ups + This month (stacked) */}
        <div className="flex flex-col gap-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="tile-followups">
          <div>
            <SectionHead icon={CalendarClock} accent="warning" title="Follow-ups" subtitle={`${(d.overdue ?? 0) + (d.due_today ?? 0)} to action`} onView={() => navigate("/followups")} />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => navigate("/followups")} data-testid="dash-overdue" className="rounded-xl bg-rose-50 p-3 text-left transition-colors hover:bg-rose-100"><div className="font-display text-2xl font-bold text-rose-700">{d.overdue ?? 0}</div><div className="text-[10px] font-bold uppercase tracking-wide text-rose-600/80">Overdue</div></button>
              <div className="rounded-xl bg-slate-50 p-3"><div className="font-display text-2xl font-bold text-ink">{d.due_today ?? 0}</div><div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Due today</div></div>
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <button onClick={() => navigate("/calendar")} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-primary/30" data-testid="dash-calendar"><span className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Calendar</span><ArrowRight className="h-4 w-4 text-muted-foreground" /></button>
              <button onClick={() => navigate("/pipeline")} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-primary/30" data-testid="dash-pipeline-link"><span className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" />Pipeline</span><ArrowRight className="h-4 w-4 text-muted-foreground" /></button>
            </div>
          </div>
          <div>
            <SectionHead icon={TrendingUp} accent="emerald" title={`This ${range === "this_quarter" ? "quarter" : "month"}`} />
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-emerald-50 p-4"><div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-emerald-700"><Trophy className="h-3.5 w-3.5" />Won {rangeLabel}</div><div className="mt-1 font-display text-2xl font-bold text-emerald-700">{money(d.won_value_month)}</div><div className="text-[11px] text-emerald-600/80">{d.won_month ?? 0} deals · {d.conversion ?? 0}%</div></div>
              <div className="rounded-xl bg-slate-50 p-4"><div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Quotes {rangeLabel}</div><div className="mt-1 font-display text-2xl font-bold text-ink">{d.quotes_month ?? 0}</div><div className="text-[11px] text-muted-foreground">{d.total_leads ?? 0} leads</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline by stage — full width */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="tile-pipeline">
        <div className="mb-5 flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /><h2 className="font-display text-base font-bold text-ink">Pipeline by stage</h2></div>
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {STAGE_ORDER.map((s) => {
            const st = d.stages?.[s] || { count: 0, value: 0 };
            return (
              <div key={s} data-testid={`stage-bar-${s}`}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><StatusBadge status={s} /><span className="text-muted-foreground">{st.count}</span></span>
                  <span className="font-display font-bold text-ink">{money(st.value)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted"><div className={cn("h-full rounded-full transition-all", STAGE_BAR[s])} style={{ width: `${Math.max(3, (st.value / stageMax) * 100)}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
